import { mdiCheckCircleOutline, mdiMinus } from '@mdi/js';
import Icon from '@mdi/react';
import React from 'react';
import { IPayerTransaction } from '../../services/payer-routing';
import { Variables } from '../base/variables';
import * as S from './payer-drawer.styles';

/**
 * The design's TRANSACTION / SERVED BY table: which of the nine X12 transactions anything
 * carries, and which connection carries it.
 *
 * Extracted because the design puts it in BOTH the expanded row and the detail drawer, and it
 * is the one view that answers "can this payer take an eligibility check at all" — a question
 * the per-connection chips cannot answer when a payer has two connections that each carry part
 * of the set.
 *
 * `loading` is a separate prop rather than inferred from the rows, because
 * `getTransactionCoverage` returns every transaction UNSERVED for undefined bindings. Rendering
 * that as "No connection — drops to paper" would state a routing fact from a request still in
 * flight (NS-3), so an unresolved join shows the ellipsis instead.
 */
export const PayerTransactionTable: React.FunctionComponent<{
    coverage: IPayerTransaction[];
    loading: boolean;
}> = ({ coverage, loading }) => (
    <S.Table>
        <S.TxRow head={true}>
            <S.TxHead>Transaction</S.TxHead>
            <S.TxHead>Served by</S.TxHead>
        </S.TxRow>
        {/* While the join is in flight every row would render grey, minus-icon and "drops to
            paper" — nine confident negatives about routing computed from connections nobody
            has loaded (NS-3). One honest row instead. */}
        {loading ? (
            <S.TxRow>
                <S.Muted>Loading clearinghouse connections…</S.Muted>
                <S.Muted>…</S.Muted>
            </S.TxRow>
        ) : (
            coverage.map(tx => (
            <S.TxRow key={tx.transaction}>
                <S.TxName served={!!tx.servedBy}>
                    <Icon
                        path={tx.servedBy ? mdiCheckCircleOutline : mdiMinus}
                        size={0.5}
                        color={
                            tx.servedBy ? Variables.ThemeLight.SuccessConstant6 : Variables.ThemeLight.TextXMild
                        }
                    />
                    {tx.transaction}
                    <S.TxLabel>{tx.label}</S.TxLabel>
                </S.TxName>
                <S.Muted>
                    {tx.servedBy
                        ? `${tx.servedBy.clearinghouse?.name || 'Clearinghouse'} · ${
                              tx.servedBy.payer?.name || tx.servedBy.externalId || ''
                          }`
                        : loading
                          ? '…'
                          : 'No connection — drops to paper'}
                </S.Muted>
                </S.TxRow>
            ))
        )}
    </S.Table>
);
